import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, FileText, Eye, Check, X, Loader2, AlertTriangle, RefreshCw, Sparkles, ShieldCheck, ListChecks, CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import DocViewModal from "../components/DocViewModal";
import { JOB_STATUS, ProgressBar } from "./Imports";

const TYPE_LABELS = { policy: "Política", procedure: "Procedimiento", record: "Registro", plan: "Plan", soa: "SoA", risk_assessment: "Análisis de riesgos" };

function confColor(c) {
  if (c >= 0.8) return "text-emerald-700 bg-emerald-100 border-emerald-200";
  if (c >= 0.5) return "text-amber-800 bg-amber-100 border-amber-200";
  return "text-slate-600 bg-slate-100";
}

export default function ImportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = ["owner", "admin", "compliance_manager"].includes(user?.role);

  const [detail, setDetail] = useState(null);
  const [viewDocId, setViewDocId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/imports/${id}`);
      setDetail(data);
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/importar");
      return null;
    }
  }, [id, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/imports/${id}`);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/importar"); }
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // Polling mientras está en progreso
  useEffect(() => {
    const status = detail?.job?.status;
    const active = ["uploaded", "extracting", "classifying"].includes(status);
    if (!active) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/imports/${id}`);
        setDetail(data);
      } catch { /* reintento en el siguiente ciclo */ }
    }, 4000);
    return () => clearInterval(t);
  }, [detail?.job?.status, id]);

  if (!detail) return <div className="text-slate-500">Cargando importación...</div>;

  const { job, documents, failed_files } = detail;
  const st = JOB_STATUS[job.status] || JOB_STATUS.uploaded;
  const inProgress = ["uploaded", "extracting", "classifying"].includes(job.status);
  const allSuggestions = documents.flatMap((d) => d.suggestions);
  const acceptedCount = allSuggestions.filter((s) => s.status === "accepted").length;

  const review = async (sid, status) => {
    try {
      await api.put(`/imports/${id}/suggestions/${sid}`, { status });
      setDetail((prev) => ({
        ...prev,
        documents: prev.documents.map((d) => ({
          ...d,
          suggestions: d.suggestions.map((s) => (s.id === sid ? { ...s, status } : s)),
        })),
      }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/imports/${id}/apply`);
      const parts = [`${data.linked_controls} control(es)`, `${data.linked_clauses} cláusula(s)`];
      toast.success(`Aplicado: ${parts.join(", ")} vinculados.`);
      if (data.suggested_status_changes?.length > 0) {
        toast.info(`Sugerencia: ${data.suggested_status_changes.length} control(es) podrían pasar a "En progreso" (no se ha cambiado automáticamente).`);
      }
      if (data.applicability_changes?.length > 0) {
        toast.info(`${data.applicability_changes.length} control(es) marcados como "Aplicable" con justificación para la Declaración de Aplicabilidad (SoA).`);
      }
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const reclassify = async () => {
    setReclassifying(true);
    try {
      await api.post(`/imports/${id}/reclassify`);
      toast.success("Reclasificando con IA...");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReclassifying(false); }
  };

  return (
    <div data-testid="import-detail-page" className="max-w-4xl">
      <Button variant="ghost" onClick={() => navigate("/importar")} className="mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Importaciones</Button>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-semibold text-slate-900">Importación · {documents.length} documento(s)</span>
            <Badge className={st.cls} data-testid="import-status-badge">{st.label}</Badge>
          </div>
          {job.status === "completed" && (
            <Button variant="outline" size="sm" onClick={() => navigate("/documents")} data-testid="go-documents">Ver en Documentos</Button>
          )}
        </div>
        <ProgressBar status={job.status} />
        {inProgress && (
          <div className="text-[13px] text-slate-500 mt-3 flex items-center gap-2" data-testid="import-processing">
            <Loader2 className="h-4 w-4 animate-spin" /> Procesando con IA. Esta pantalla se actualizará automáticamente...
          </div>
        )}
        {job.status === "error" && (
          <div className="text-[13px] text-red-700 mt-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {job.error_message || "Error procesando la importación."}</div>
        )}
      </div>

      {/* Archivos con error */}
      {failed_files?.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="failed-files">
          <div className="text-[13px] font-semibold text-amber-800 flex items-center gap-1 mb-2"><AlertTriangle className="h-4 w-4" /> Archivos no procesados</div>
          {failed_files.map((f, i) => (
            <div key={i} className="text-[13px] text-amber-900 flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" /> <b>{f.filename}</b> — {f.error}
            </div>
          ))}
        </div>
      )}

      {/* Documentos y sugerencias */}
      {!inProgress && documents.length > 0 && (
        <>
          <div className="mt-6 flex items-center justify-between">
            <div className="text-[12px] uppercase tracking-wide text-slate-400">Documentos y sugerencias de la IA</div>
            {canManage && job.status === "review_pending" && (
              <Button variant="ghost" size="sm" onClick={reclassify} disabled={reclassifying} data-testid="reclassify-btn">
                {reclassifying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Reclasificar
              </Button>
            )}
          </div>

          <div className="mt-3 space-y-4">
            {documents.map((doc) => (
              <div key={doc.document_id} className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={`import-doc-${doc.document_id}`}>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/60">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="text-[14px] font-medium text-slate-900">{doc.title}</span>
                  <Badge variant="outline" className="text-slate-500">{TYPE_LABELS[doc.type] || doc.type}</Badge>
                  <Badge className="bg-violet-100 text-violet-700 border-violet-200">Importado</Badge>
                  <div className="flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => setViewDocId(doc.document_id)} data-testid={`import-consultar-${doc.document_id}`}><Eye className="h-4 w-4 mr-1" /> Consultar</Button>
                </div>
                <div className="divide-y divide-slate-100">
                  {doc.suggestions.length === 0 && <div className="px-4 py-4 text-[13px] text-slate-400">La IA no encontró vinculaciones claras para este documento.</div>}
                  {doc.suggestions.map((s) => {
                    const pct = Math.round((s.confidence || 0) * 100);
                    return (
                      <div key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`suggestion-${s.id}`}>
                        <div className="mt-0.5">
                          {s.target_type === "control"
                            ? <ShieldCheck className="h-4 w-4 text-sky-500" />
                            : <ListChecks className="h-4 w-4 text-indigo-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14px] font-medium text-slate-900">
                              {s.target_type === "control" ? `Control ${s.target_code}` : `Cláusula · paso ${s.target_code}`}
                            </span>
                            <span className={`text-[11px] border rounded px-1.5 py-0.5 ${confColor(s.confidence)}`}>{pct}% confianza</span>
                          </div>
                          <p className="text-[13px] text-slate-600 mt-1">{s.rationale}</p>
                          {s.target_type === "control" && s.proposed_applicability && (
                            <div className="mt-1.5 flex items-start gap-1.5 flex-wrap" data-testid={`applicability-proposal-${s.id}`}>
                              <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[11px]">
                                IA propone: {s.proposed_applicability === "applicable" ? "Aplicable (SoA)" : "No aplicable"}
                              </Badge>
                              {s.applicability_justification && (
                                <span className="text-[12px] text-slate-500 italic">{s.applicability_justification}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {job.status === "review_pending" && canManage ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant={s.status === "accepted" ? "default" : "outline"}
                              size="sm"
                              onClick={() => review(s.id, "accepted")}
                              data-testid={`accept-${s.id}`}
                              className={s.status === "accepted" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={s.status === "rejected" ? "default" : "outline"}
                              size="sm"
                              onClick={() => review(s.id, "rejected")}
                              data-testid={`reject-${s.id}`}
                              className={s.status === "rejected" ? "bg-slate-500 hover:bg-slate-600" : ""}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Badge className={s.status === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : s.status === "rejected" ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-500"}>
                            {s.status === "accepted" ? "Aceptada" : s.status === "rejected" ? "Rechazada" : "Pendiente"}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {job.status === "review_pending" && canManage && (
            <div className="mt-5 flex items-center justify-between bg-white border border-slate-200 rounded-md p-4 sticky bottom-4 shadow-sm">
              <span className="text-[13px] text-slate-600">{acceptedCount} sugerencia(s) aceptada(s)</span>
              <Button onClick={apply} disabled={applying || acceptedCount === 0} data-testid="apply-btn">
                {applying ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Aplicando...</> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Aplicar sugerencias aceptadas</>}
              </Button>
            </div>
          )}

          {job.status === "completed" && (
            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-[13px] text-emerald-800 flex items-center gap-2" data-testid="import-completed-banner">
              <CheckCircle2 className="h-4 w-4" /> Importación aplicada. Los documentos quedaron vinculados a sus controles y pasos. Puedes consultarlos, editarlos y archivarlos desde Documentos.
            </div>
          )}
        </>
      )}

      {viewDocId && <DocViewModal docId={viewDocId} onClose={() => setViewDocId(null)} />}
    </div>
  );
}
